import { useCallback, useEffect, useRef, useState } from "react";
import type { ChangeEvent } from "react";
import { AlertCircle, CheckCircle2, Download, RefreshCw } from "lucide-react";
import { getUserFacingErrorMessage } from "@/api/userFacingError";
import {
  downloadQuotationExportPdf,
  getEffectiveQuotation,
  getQuotationExportData,
} from "@/features/quotation/api";
import type {
  DailyQuotation,
  EffectiveQuotation,
  QuotationExportDocument,
} from "@/features/quotation/types";
import { useClients } from "@/features/client/hooks";
import { useStores } from "@/features/store/hooks";
import { formatDateBR } from "@/lib/timeAgo";
import { ReportPreview } from "@/components/report-print/ReportPreview";
import { ExportDialogFrame } from "@/components/report-print/ExportDialogFrame";
import { QuotationPrintDocument } from "./QuotationPrintDocument";
import { useDialogFocus } from "@/hooks/useDialogFocus";

interface QuotationExportModalProps {
  quotation: Pick<DailyQuotation, "id" | "date" | "status">;
  onClose: () => void;
  loadExportData?: (quotationId: string) => Promise<QuotationExportDocument>;
  loadEffectiveQuotation?: typeof getEffectiveQuotation;
  downloadExportPdf?: typeof downloadQuotationExportPdf;
}

const ACCENT = "var(--export-accent)";
const ACCENT_DIM =
  "color-mix(in srgb, var(--export-accent) 12%, var(--export-surface))";
const ACCENT_BORDER =
  "color-mix(in srgb, var(--export-accent) 26%, var(--export-border))";

export function QuotationExportModal({
  quotation,
  onClose,
  loadExportData = getQuotationExportData,
  loadEffectiveQuotation = getEffectiveQuotation,
  downloadExportPdf = downloadQuotationExportPdf,
}: QuotationExportModalProps) {
  const { data: clientsData } = useClients();
  const { data: storesData } = useStores();
  const [targetValue, setTargetValue] = useState("none");
  const [targetLabel, setTargetLabel] = useState("Sem alvo");
  const [documentData, setDocumentData] =
    useState<QuotationExportDocument | null>(null);
  const [exportQuotationId, setExportQuotationId] = useState<string | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const requestSequence = useRef(0);
  const storesDataRef = useRef(storesData);
  storesDataRef.current = storesData;

  const close = () => {
    if (!isDownloadingPdf) onClose();
  };
  useDialogFocus(dialogRef, close, { initialFocusRef: closeRef });

  const resolveExportQuotationId = useCallback(async () => {
    if (targetValue === "none") return quotation.id;

    const [kind, id] = targetValue.split(":");
    const params: Parameters<typeof getEffectiveQuotation>[0] = {
      date: quotation.date,
    };
    if (kind === "client") params.clientId = id;
    if (kind === "store") {
      params.storeId = id;
      const selectedStore = storesDataRef.current?.items.find(
        (store) => store.id === id,
      );
      if (selectedStore?.client_id) {
        params.clientId = selectedStore.client_id;
      }
    }

    const effective: EffectiveQuotation = await loadEffectiveQuotation(params);
    return effective.id;
  }, [loadEffectiveQuotation, quotation.date, quotation.id, targetValue]);

  const fetchExport = useCallback(async () => {
    const requestId = requestSequence.current + 1;
    requestSequence.current = requestId;
    setLoading(true);
    setError(null);
    setDocumentData(null);
    setExportQuotationId(null);
    try {
      const exportQuotationId = await resolveExportQuotationId();
      const data = await loadExportData(exportQuotationId);
      if (requestSequence.current !== requestId) return;
      setDocumentData(data);
      setExportQuotationId(exportQuotationId);
    } catch (e) {
      if (requestSequence.current !== requestId) return;
      setDocumentData(null);
      setExportQuotationId(null);
      setError(
        getUserFacingErrorMessage(
          e,
          "Não foi possível gerar a cotação para impressão.",
        ),
      );
    } finally {
      if (requestSequence.current === requestId) {
        setLoading(false);
      }
    }
  }, [loadExportData, resolveExportQuotationId]);

  useEffect(() => {
    void fetchExport();
  }, [fetchExport]);

  async function handleDownloadPdf() {
    if (!documentData || !exportQuotationId || isDownloadingPdf) return;

    setIsDownloadingPdf(true);
    setError(null);
    try {
      const { blob, filename } = await downloadExportPdf(exportQuotationId);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename ?? `cotacao-clientes-${quotation.date}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      setError("Não foi possível baixar o PDF da cotação.");
    } finally {
      setIsDownloadingPdf(false);
    }
  }

  function handleTargetChange(event: ChangeEvent<HTMLSelectElement>) {
    requestSequence.current += 1;
    setTargetValue(event.target.value);
    setTargetLabel(
      event.target.selectedOptions[0]?.textContent?.trim() || "Sem alvo",
    );
    setDocumentData(null);
    setExportQuotationId(null);
    setError(null);
    setLoading(true);
  }

  const statusLabel = isDownloadingPdf
    ? "Gerando PDF"
    : loading
      ? "Gerando arquivo"
      : documentData
        ? "Arquivo pronto"
        : error
          ? "Falha ao gerar"
          : "Preparando arquivo";

  return (
    <>
      <style>{`
        @keyframes quotationExportSpin { to { transform: rotate(360deg); } }
        .quotation-export-body {
          grid-template-columns: minmax(280px, 360px) minmax(0, 1fr);
        }
        .quotation-export-primary-btn,
        .quotation-export-secondary-btn {
          transition: opacity 0.16s ease, background-color 0.16s ease;
        }
        .quotation-export-primary-btn:hover:not(:disabled),
        .quotation-export-secondary-btn:hover:not(:disabled) {
          filter: brightness(0.96);
        }
        .quotation-export-primary-btn:disabled,
        .quotation-export-secondary-btn:disabled {
          opacity: 0.55;
          cursor: not-allowed;
        }
        .quotation-export-spin {
          animation: quotationExportSpin 0.8s linear infinite;
        }
        @media print {
          .quotation-export-overlay {
            display: none !important;
          }
        }
        @media (max-width: 899px) {
          .quotation-export-body {
            grid-template-columns: 1fr !important;
            padding-bottom: calc(1.75rem + env(safe-area-inset-bottom)) !important;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .quotation-export-spin { animation: none !important; }
          .quotation-export-primary-btn, .quotation-export-secondary-btn { transition: none !important; }
        }
      `}</style>

      <ExportDialogFrame
        titleId="quotation-export-title"
        eyebrow="Cotação para clientes"
        title={formatDateBR(quotation.date)}
        dialogRef={dialogRef}
        closeRef={closeRef}
        onClose={close}
        closeDisabled={isDownloadingPdf}
        wide
        overlayClassName="quotation-export-overlay"
        panelClassName="quotation-export-panel"
      >
        <div
          className="quotation-export-body"
          style={{ padding: "1rem", display: "grid", gap: "1rem" }}
        >
          <div
            style={{
              display: "grid",
              gap: "1rem",
              alignContent: "start",
            }}
          >
            <div
              style={{
                padding: "0.95rem",
                borderRadius: "1rem",
                border: `1px solid ${
                  error ? "rgba(239,68,68,0.28)" : ACCENT_BORDER
                }`,
                background: error ? "rgba(239,68,68,0.12)" : ACCENT_DIM,
              }}
            >
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.4rem",
                  color: error ? "#ef4444" : ACCENT,
                  fontFamily: "'Barlow Condensed', sans-serif",
                  fontSize: "0.86rem",
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                }}
              >
                {loading || isDownloadingPdf ? (
                  <RefreshCw size={15} className="quotation-export-spin" />
                ) : documentData ? (
                  <CheckCircle2 size={15} />
                ) : (
                  <AlertCircle size={15} />
                )}
                {statusLabel}
              </div>
              <p
                style={{
                  margin: "0.45rem 0 0",
                  color: "var(--login-text)",
                  fontSize: "0.875rem",
                  lineHeight: 1.45,
                }}
              >
                {documentData
                  ? "Arquivo pronto para baixar em PDF."
                  : loading
                    ? "Preparando arquivo para prévia."
                    : error
                      ? "Não foi possível preparar o PDF."
                      : "Preparando arquivo."}
              </p>
            </div>

            <InfoRow
              label="Status da cotação"
              value={quotation.status === "completed" ? "Concluída" : "Aberta"}
            />

            <label
              style={{
                display: "grid",
                gap: "0.4rem",
              }}
            >
              <span
                style={{
                  fontFamily: "'Barlow Condensed', sans-serif",
                  fontWeight: 700,
                  fontSize: "0.72rem",
                  letterSpacing: "0.08em",
                  color: "var(--login-muted)",
                  textTransform: "uppercase",
                }}
              >
                Alvo da cotação
              </span>
              <select
                aria-label="Alvo da cotacao"
                value={targetValue}
                onChange={handleTargetChange}
                style={{
                  minHeight: "2.85rem",
                  borderRadius: "0.9rem",
                  border: "1px solid var(--login-border)",
                  background: "var(--login-bg)",
                  color: "var(--login-text)",
                  padding: "0 0.85rem",
                  fontSize: "0.92rem",
                  outline: "none",
                }}
              >
                <option value="none">Sem alvo</option>
                {clientsData?.items.map((client) => (
                  <option key={client.id} value={`client:${client.id}`}>
                    Cliente · {client.name}
                  </option>
                ))}
                {storesData?.items.map((store) => (
                  <option key={store.id} value={`store:${store.id}`}>
                    Loja · {store.name}
                  </option>
                ))}
              </select>
            </label>

            {error && !loading && (
              <div
                role="alert"
                style={{
                  display: "flex",
                  gap: "0.6rem",
                  alignItems: "center",
                  padding: "0.8rem 0.9rem",
                  borderRadius: "0.9rem",
                  border: "1px solid rgba(239,68,68,0.28)",
                  background: "rgba(239,68,68,0.12)",
                  color: "var(--login-text)",
                  fontSize: "0.9rem",
                }}
              >
                <AlertCircle size={16} color="#ef4444" />
                {error}
              </div>
            )}

            <div style={{ display: "grid", gap: "0.65rem" }}>
              {documentData && (
                <button
                  type="button"
                  className="quotation-export-primary-btn"
                  onClick={handleDownloadPdf}
                  disabled={isDownloadingPdf || !exportQuotationId}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "0.55rem",
                    minHeight: "3rem",
                    borderRadius: "0.9rem",
                    border: "none",
                    background: ACCENT,
                    color: "#08111f",
                    fontFamily: "'Barlow Condensed', sans-serif",
                    fontWeight: 700,
                    fontSize: "1rem",
                    letterSpacing: "0.06em",
                    cursor: "pointer",
                  }}
                >
                  {isDownloadingPdf ? (
                    <RefreshCw size={15} className="quotation-export-spin" />
                  ) : (
                    <Download size={15} />
                  )}
                  {isDownloadingPdf ? "Gerando PDF..." : "Baixar PDF"}
                </button>
              )}

              {error && !loading && (
                <button
                  type="button"
                  className="quotation-export-secondary-btn"
                  onClick={fetchExport}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "0.55rem",
                    minHeight: "2.85rem",
                    borderRadius: "0.9rem",
                    border: `1px solid ${ACCENT_BORDER}`,
                    background: "transparent",
                    color: ACCENT,
                    fontFamily: "'Barlow Condensed', sans-serif",
                    fontWeight: 700,
                    fontSize: "0.95rem",
                    letterSpacing: "0.06em",
                    cursor: "pointer",
                  }}
                >
                  <RefreshCw size={15} />
                  Tentar novamente
                </button>
              )}
            </div>
          </div>

          {documentData && (
            <div className="quotation-export-preview">
              <div className="report-print-preview-document">
                <ReportPreview>
                  <QuotationPrintDocument
                    document={documentData}
                    printMode="preview"
                    targetLabel={
                      targetValue === "none" ? undefined : targetLabel
                    }
                  />
                </ReportPreview>
              </div>
            </div>
          )}
        </div>
      </ExportDialogFrame>
    </>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        padding: "0.8rem 0.9rem",
        borderRadius: "0.9rem",
        border: "1px solid var(--login-border)",
        background: "var(--login-bg)",
      }}
    >
      <div
        style={{
          fontFamily: "'Barlow Condensed', sans-serif",
          fontWeight: 700,
          fontSize: "0.72rem",
          letterSpacing: "0.08em",
          color: "var(--login-muted)",
          textTransform: "uppercase",
        }}
      >
        {label}
      </div>
      <div
        style={{
          marginTop: "0.28rem",
          color: "var(--login-text)",
          fontSize: "0.9rem",
        }}
      >
        {value}
      </div>
    </div>
  );
}
